// Import React and necessary hooks
import React, { useState, useEffect } from "react";

// Test component displays the quiz for the selected topic
// Props:
// - topic: the selected topic from Home.js
// - goBack: function from App.js to return to Home page
function Test({ topic, goBack }) {
  // Store all questions fetched from API
  const [questions, setQuestions] = useState([]);
  
  // Track the index of the current question
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  // Store the answer selected by the user
  const [selectedAnswer, setSelectedAnswer] = useState("");
  
  // Store the user's score
  const [score, setScore] = useState(0);
  
  // Track whether the quiz is completed
  const [quizCompleted, setQuizCompleted] = useState(false);
  
  // Track loading state while fetching questions
  const [loading, setLoading] = useState(true);

  // Map topic names to Open Trivia DB category IDs
  const categories = {
    General: 9,
    Science: 17,
    Math: 19,
    History: 23,
    Sports: 21,
  };

  // Helper function: decode HTML entities returned by the API
  const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // Helper function: shuffle array (used for randomizing answers)
  const shuffleArray = (array) =>
    array
      .map((v) => ({ value: v, sort: Math.random() })) // Add random sort key
      .sort((a, b) => a.sort - b.sort)                 // Sort randomly
      .map(({ value }) => value);                      // Return only values

  // Fetch questions from API based on the topic
  const fetchQuestionsForTopic = async (topicName) => {
    setLoading(true);        // Show loading spinner
    setQuizCompleted(false); // Reset quiz completed state
    setScore(0);             // Reset score
    setCurrentQuestion(0);   // Start from first question
    setSelectedAnswer("");   // Reset selected answer
    setQuestions([]);        // Clear previous questions

    try {
      // Get category ID, fallback to General if topic not found
      const categoryId = categories[topicName] || 9;

      // Fetch 3 multiple-choice questions from Open Trivia DB
      const response = await fetch(
        `https://opentdb.com/api.php?amount=3&category=${categoryId}&type=multiple`
      );

      // Convert response to JSON
      const data = await response.json();

      // If API returns no questions
      if (!data.results || data.results.length === 0) {
        if (topicName !== "General") {
          // Inform user and fallback to General topic
          alert(
            `Sorry, no questions available for "${topicName}". Falling back to General.`
          );
          fetchQuestionsForTopic("General"); // Recursive fetch
        } else {
          // No questions even in General, go back to Home
          alert("No questions available. Please try again later.");
          goBack();
        }
        return;
      }

      // Update questions state with fetched data
      setQuestions(data.results);
    } catch (error) {
      console.error("Error fetching questions:", error);
      alert("Failed to this time. Please try again.");
      goBack(); // Return to Home if API fails
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  // useEffect: fetch questions whenever topic changes
  useEffect(() => {
    fetchQuestionsForTopic(topic);
  }, [topic]);

  // Handle answer button click
  const handleAnswer = (answer) => setSelectedAnswer(answer);

  // Move to next question or complete quiz
  const handleNext = () => {
    // Increase score if answer is correct
    if (questions[currentQuestion]?.correct_answer === selectedAnswer) {
      setScore(score + 1);
    }

    setSelectedAnswer(""); // Reset selected answer

    // Move to next question if available, else mark quiz completed
    if (currentQuestion + 1 < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setQuizCompleted(true);
    }
  };

  // Restart quiz for same topic
  const handleRestart = () => fetchQuestionsForTopic(topic);

  // Current question object (safely handle undefined)
  const currentQ = questions[currentQuestion] || {};

  return (
    <div className="container mt-5">
      {loading ? (
        // Show spinner while fetching questions
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Fetching questions for "{topic}"...</p>
        </div>
      ) : !quizCompleted && questions.length > 0 ? (
        // Quiz questions view
        <div>
          <h3 className="quiz-card">Topic: {topic}</h3>
          <p>
            Question {currentQuestion + 1}: {decodeHTML(currentQ.question || "")}
          </p>

          <div className="d-flex flex-column">
            {/* Display shuffled answer options */}
            {shuffleArray([
              ...(currentQ.incorrect_answers || []),
              currentQ.correct_answer,
            ]).map((answer, index) => (
              <button
                key={index}
                className={`btn btn-outline-primary my-1 ${
                  selectedAnswer === answer ? "active" : ""
                }`}
                onClick={() => handleAnswer(answer)}
              >
                {decodeHTML(answer || "")}
              </button>
            ))}
          </div>

          {/* Next question button */}
          <button
            className="btn btn-success mt-3"
            onClick={handleNext}
            disabled={!selectedAnswer} // Enable only after selecting answer
          >
            Next
          </button>

          {/* Back to Home button */}
          <button className="btn btn-secondary mt-3 ms-2" onClick={goBack}>
            Back to Home
          </button>
        </div>
      ) : (
        // Quiz completed view
        <div>
          <h3>Quiz Completed!</h3>
          <p>
            Your Score: {score} / {questions.length || 0}
          </p>
          <button className="btn btn-primary me-2" onClick={handleRestart}>
            Restart Quiz
          </button>
          <button className="btn btn-secondary" onClick={goBack}>
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
}

// Export Test component for App.js
export default Test;
